import React, { useEffect } from 'react';
import { Avatar, Box, Card, Chip, Grid, SvgIconProps, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { theme } from 'theme/theme';
import { AttachMoney, HowToReg, LoyaltyOutlined, SchoolOutlined } from '@mui/icons-material';
import DashboardContainer from 'components/PageContainer/DashboardContainer';
import './Dashboard.scss';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsuarios } from 'utils/redux/features/usuariosSlice';
import { RootState, AppDispatch } from 'utils/redux/store/store';
import NoResponse from 'pages/auxiliares/NoResponse';
import Spinner from 'components/public/Spinner/Spinner';
import NotAllowed from 'pages/auxiliares/NotAllowed';
import { fetchVentas } from 'utils/redux/features/ventasSlice';
import { fetchCompras } from 'utils/redux/features/comprasSlice';
import { VentaListado } from 'utils/models/types';

const normalizarTipoSocio = (tipoSocio?: string) => tipoSocio?.trim().toLowerCase() ?? '';

interface DashboardCardProps {
  height: number;
  titulo: string;
  valor?: string;
  textoDebajo?: string;
  colorAvatar: string;
  icono: React.ComponentType<SvgIconProps> | null;
  graficaVentas?: boolean;
  graficaTorta?: boolean;
  valorNosocio?: number;
  valorSocioTotal?: number;
  valorAmigoBosch?: number;
  ventas?: VentaListado[];
}

const CardItem = ({
  height,
  titulo,
  valor,
  textoDebajo,
  colorAvatar,
  icono: Icono,
  graficaVentas,
  graficaTorta,
  valorAmigoBosch,
  valorSocioTotal,
  valorNosocio,
  ventas,
}: DashboardCardProps) => {
  const totalVentas = ventas?.map(v => Number(v.MontoTotalVentaEntradas));
  const totalGanancia = ventas?.map(v => Number(v.MontoTotalGananciaEntradas));
  const nombres = ventas?.map(v => v.NombreEspectaculo);
  const datosSocios = [
    { value: valorAmigoBosch ?? 0, label: 'Amigos del Bosch', color: theme.palette.primary.main },
    { value: valorSocioTotal ?? 0, label: 'Socio Total', color: theme.palette.secondary.main },
    { value: valorNosocio ?? 0, label: 'No socios', color: theme.palette.primary.light },
  ];

  return (
    <Card
      sx={{
        height: `${height}px`,
        borderRadius: 5,
        border: '1px solid #dbdbdb',
        py: 4,
        px: 3,
        display: 'flex',
        flexDirection: graficaTorta || graficaVentas ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: graficaTorta || graficaVentas ? 'center' : '',
        boxShadow: ' 10px 2px 105px -31px rgba(130,130,130,0.1)',
        position: 'relative',
      }}
      elevation={0}
    >
      <Box display="flex" flexDirection="column" justifyContent="space-between">
        <Box>
          <Typography variant="body2" textAlign="left">
            {titulo}
          </Typography>
          <Typography variant="h4" textAlign="left" mt={1}>
            {valor}
          </Typography>
        </Box>
        {textoDebajo && (
          <>
            <Typography variant="body2" textAlign="left" fontSize="0.8rem" my={1}>
              Último registro:
            </Typography>
            <Chip label={textoDebajo} className="chipStyle" size="medium" />
          </>
        )}
      </Box>
      {!graficaTorta && !graficaVentas && Icono && (
        <Avatar sx={{ bgcolor: colorAvatar, position: 'absolute', right: '24px', top: '58px' }}>{<Icono />}</Avatar>
      )}
      {graficaVentas && (
        <BarChart
          xAxis={[{ scaleType: 'band', data: nombres }]}
          series={[
            { data: totalVentas, stack: 'A', label: 'Monto generado', color: theme.palette.primary.main },
            { data: totalGanancia, stack: 'A', label: 'Ganancia', color: theme.palette.primary.light },
          ]}
          barLabel={(item, context) => {
            return context.bar.height < 60 ? null : item.value?.toString();
          }}
          height={450}
        />
      )}

      {graficaTorta && (
        <Box className="dashboardPie">
          <Box className="dashboardPie__chart">
            <PieChart
              series={[
                {
                  data: datosSocios,
                  innerRadius: 60,
                  outerRadius: 100,
                },
              ]}
              height={280}
              margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
              legend={{ hidden: true }}
            />
          </Box>
          <Box component="ul" className="dashboardPie__legend">
            {datosSocios.map(({ color, label }) => (
              <Box component="li" className="dashboardPie__legendItem" key={label}>
                <Box component="span" className="dashboardPie__legendColor" sx={{ backgroundColor: color }} />
                <Typography component="span" variant="body1">
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Card>
  );
};

const Dashboard = () => {
  const { usuarios, loadingUsuarios, errorUsuarios } = useSelector((state: RootState) => state.usuarios);
  const { ventas, loadingVentas, errorVentas } = useSelector((state: RootState) => state.ventas);
  const { compras, loadingCompras, errorCompras } = useSelector((state: RootState) => state.compras);
  const dispatch = useDispatch<AppDispatch>();
  const error401 = errorUsuarios?.includes('permisos');
  const ultimoUsuarioRegistrado = !loadingUsuarios
    ? usuarios?.reduce(
        (maxUsu, usu) => (usu.Id > maxUsu.Id ? usu : maxUsu),
        usuarios[0] ?? null // Valor inicial como el primer elemento o null si está vacío
      )
    : null;

  const alumnos = usuarios.filter(u => u.EsAlumno);
  const ultimoAlumnoRegistrado =
    alumnos.length > 0 ? alumnos.reduce((maxUsu, usu) => (usu.Id > maxUsu.Id ? usu : maxUsu)) : null;

  const sociosTotales =
    usuarios && usuarios.filter(u => normalizarTipoSocio(u.TipoSocio) === 'socio total').length;
  const sociosAmigos =
    usuarios && usuarios.filter(u => normalizarTipoSocio(u.TipoSocio) === 'amigos del bosch').length;
  const noSocios =
    usuarios &&
    usuarios.filter(u => {
      const tipoSocio = normalizarTipoSocio(u.TipoSocio);
      return tipoSocio !== 'socio total' && tipoSocio !== 'amigos del bosch';
    }).length;
  const sociosAlumnos = usuarios && usuarios.filter(u => u.EsAlumno).length;
  const totalEntradasVendidas =
    ventas &&
    ventas?.reduce((total, element) => {
      return total + element.CantEntradasVendidas;
    }, 0);

  const ultimaCompra =
    !loadingCompras && compras && compras.length > 0
      ? compras.reduce((max, compra) => (compra?.Id > max.Id ? compra : max))
      : null;

  const gananciaTotal =
    ventas?.reduce((total, venta) => {
      const ganancia =
        typeof venta.MontoTotalGananciaEntradas === 'string'
          ? parseFloat(venta.MontoTotalGananciaEntradas)
          : venta.MontoTotalGananciaEntradas;

      return total + ganancia;
    }, 0) ?? 0;

  useEffect(() => {
    dispatch(fetchUsuarios());
    dispatch(fetchVentas());
    dispatch(fetchCompras());
  }, [dispatch]);

  useEffect(() => {
    localStorage.removeItem('urlAnterior');
  }, []);

  return loadingUsuarios || loadingVentas || loadingCompras ? (
    <Spinner />
  ) : errorUsuarios || errorVentas || errorCompras ? (
    error401 ? (
      <NotAllowed />
    ) : (
      <NoResponse />
    )
  ) : (
    <DashboardContainer>
      <Box mx={4}>
        <Grid container py={4} spacing={3}>
          <Grid item xs={12} lg={6} xl={3}>
            <CardItem
              height={130}
              titulo={'total de usuarios'.toUpperCase()}
              valor={usuarios.length.toString()}
              colorAvatar={theme.palette.primary.dark}
              icono={HowToReg}
              textoDebajo={
                ultimoUsuarioRegistrado != null
                  ? `${ultimoUsuarioRegistrado.Nombre} ${ultimoUsuarioRegistrado.Apellido}`
                  : 'Sin datos'
              }
            />
          </Grid>
          <Grid item xs={12} lg={6} xl={3}>
            <CardItem
              height={130}
              titulo={'entradas vendidas'.toUpperCase()}
              valor={totalEntradasVendidas.toString()}
              colorAvatar={theme.palette.primary.main}
              icono={LoyaltyOutlined}
              textoDebajo={ultimaCompra ? ultimaCompra.NombreEspectaculo : 'Sin datos'}
            />
          </Grid>

          <Grid item xs={12} lg={6} xl={3}>
            <CardItem
              height={130}
              titulo={'total de ganancia'.toUpperCase()}
              valor={`$${gananciaTotal.toFixed(2)}`}
              colorAvatar={theme.palette.primary.light}
              icono={AttachMoney}
              textoDebajo={ultimaCompra ? `$${ultimaCompra.GananciaTeatroSinFijoSala}` : 'Sin datos'}
            />
          </Grid>

          <Grid item xs={12} lg={6} xl={3}>
            <CardItem
              height={130}
              titulo={'total de alumnos'.toUpperCase()}
              valor={sociosAlumnos.toString()}
              colorAvatar={theme.palette.secondary.main}
              icono={SchoolOutlined}
              textoDebajo={
                ultimoAlumnoRegistrado != null
                  ? `${ultimoAlumnoRegistrado.Nombre} ${ultimoAlumnoRegistrado.Apellido}`
                  : 'Sin datos'
              }
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <CardItem
              height={500}
              titulo={'ganancia por espectáculo'.toUpperCase()}
              colorAvatar={'green'}
              graficaVentas
              ventas={ventas}
              icono={null}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <CardItem
              height={500}
              titulo={'usuarios / socios'.toUpperCase()}
              colorAvatar={''}
              graficaTorta
              valorSocioTotal={sociosTotales}
              valorAmigoBosch={sociosAmigos}
              valorNosocio={noSocios}
              icono={null}
            />
          </Grid>
        </Grid>
      </Box>
    </DashboardContainer>
  );
};

export default Dashboard;
